import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Servizi',   href: '/servizi' },
  { label: 'Progetti',  href: '/progetti' },
  { label: 'Collabora', href: '/collabora' },
];

export default function Navbar() {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [hovered,   setHovered]   = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <>
      <motion.nav
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          padding: '0 2rem', height: '68px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(24px)' : 'none',
          backgroundColor: scrolled ? 'rgba(5,5,10,0.82)' : 'transparent',
          borderBottom: `1px solid ${scrolled ? 'rgba(255,255,255,0.06)' : 'transparent'}`,
          transition: 'background-color 0.35s ease, border-color 0.35s ease',
        }}
      >
        {/* ── Logo ── */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', minHeight: 44, textDecoration: 'none' }}>
          {/* Desktop: logo esteso */}
          <img
            src="/logo-extended.webp"
            alt="VMDProject"
            width={108}
            height={38}
            className="logo-extended"
            style={{ height: 38, width: 'auto', mixBlendMode: 'screen', display: 'block' }}
          />
          {/* Mobile: logo quadrato */}
          <img
            src="/logo-nav.webp"
            alt="VMDProject"
            width={38}
            height={38}
            className="logo-square"
            style={{ height: 38, width: 38, mixBlendMode: 'screen', display: 'none', objectFit: 'contain' }}
          />
        </Link>

        {/* ── Desktop links ── */}
        <div className="nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {NAV_LINKS.map(({ label, href }) => {
            const active  = location.pathname === href;
            const isHov   = hovered === href;
            return (
              <Link key={href} to={href}
                onMouseEnter={() => setHovered(href)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  fontFamily: 'var(--font-body)', fontWeight: active ? 600 : 500, fontSize: '0.95rem',
                  color: isHov ? 'var(--color-text)' : 'var(--color-muted)',
                  padding: '7px 14px',
                  borderRadius: '8px',
                  background: isHov && !active ? 'rgba(255,255,255,0.05)' : 'transparent',
                  border: '1px solid transparent',
                  transition: 'all 0.18s ease',
                  position: 'relative',
                  textDecoration: 'none',
                  ...(active ? {
                    background: 'var(--gradient-text)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  } : {}),
                }}
              >
                {label}
                {active && (
                  <motion.span
                    layoutId="nav-indicator"
                    style={{
                      position: 'absolute', bottom: -1, left: '14px', right: '14px',
                      height: '2px',
                      background: 'linear-gradient(90deg, #7c3aed, #f472b6)',
                      borderRadius: '2px', display: 'block',
                      boxShadow: '0 0 8px rgba(196,181,253,0.6), 0 0 16px rgba(244,114,182,0.3)',
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}

          {/* Separatore */}
          <div style={{
            width: 1, height: 20,
            background: 'rgba(255,255,255,0.1)',
            margin: '0 0.85rem',
            flexShrink: 0,
          }} />

          <Link to="/collabora" className="btn-primary"
            style={{ padding: '9px 22px', fontSize: '0.9rem', gap: '7px' }}
          >
            Iniziamo
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
              <path d="M1 6h10M7 2l4 4-4 4" stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>

        {/* ── Hamburger mobile ── */}
        <button
          onClick={() => setMenuOpen(o => !o)}
          className="nav-mobile-toggle"
          aria-label="Menu"
          style={{
            background: menuOpen ? 'rgba(124,58,237,0.15)' : 'transparent',
            border: `1px solid ${menuOpen ? 'rgba(124,58,237,0.4)' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: '8px', minWidth: 44, minHeight: 44, cursor: 'pointer',
            color: 'var(--color-text)', display: 'none',
            alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s ease',
          }}
        >
          <motion.div animate={{ rotate: menuOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </motion.div>
        </button>
      </motion.nav>

      {/* ── Drawer mobile — card flottante ── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{   opacity: 0, y: -8,  scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed', top: '76px', left: '1rem', right: '1rem', zIndex: 99,
              background: 'rgba(8,8,16,0.97)',
              backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '16px',
              padding: '0.75rem',
              display: 'flex', flexDirection: 'column', gap: '0.25rem',
              boxShadow: '0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(124,58,237,0.12)',
            }}
          >
            {NAV_LINKS.map(({ label, href }) => {
              const active = location.pathname === href;
              return (
                <Link key={href} to={href}
                  style={{
                    fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: '1rem',
                    color: active ? 'var(--color-accent-lt)' : 'var(--color-text-2)',
                    padding: '0.8rem 1rem',
                    borderRadius: '10px',
                    background: active ? 'rgba(124,58,237,0.12)' : 'transparent',
                    border: active ? '1px solid rgba(124,58,237,0.22)' : '1px solid transparent',
                    transition: 'background 0.15s ease',
                    display: 'block', textDecoration: 'none',
                  }}
                >{label}</Link>
              );
            })}

            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0.35rem 0' }} />

            <Link to="/collabora" className="btn-primary"
              style={{ justifyContent: 'center', padding: '12px 20px' }}>
              Iniziamo
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 640px) {
          .nav-desktop        { display: none !important; }
          .nav-mobile-toggle  { display: flex !important; }
          .logo-extended      { display: none !important; }
          .logo-square        { display: block !important; }
        }
      `}</style>
    </>
  );
}
